import React, { useEffect, useMemo, useState, useRef } from "react";
import "./TeamMembers.css";
import SidePanel from "./SidePanel";
import { apiFetch } from "../utils/api";
import { canDo } from "../utils/permissions";
import {
  createInlineFallbackAvatar,
  getTeamMemberAvatar,
  getTeamMemberName,
  normalizeTeamMemberGender
} from "../utils/teamMemberAvatar";
import Paginator from "./Paginator";

const emptyForm = {
  firstName: "",
  lastName: "",
  gender: "female",
  country: "",
  email: "",
  countryCode: "+971",
  phone: "",
  jobTitle: "",
  avatarUrl: "",
  locations: [],
  allowBookings: true
};

const NATIONALITY_OPTIONS = [
  "",
  "UAE",
  "India",
  "Pakistan",
  "Philippines",
  "Bangladesh",
  "Nepal",
  "Sri Lanka",
  "Egypt",
  "Jordan",
  "Lebanon",
  "Saudi Arabia",
  "United Kingdom",
  "United States",
  "Canada",
  "Australia",
  "Other"
];

const TeamMembers = ({ currentUser, onMemberSaved, onMemberDeleted }) => {
  const [members, setMembers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [formError, setFormError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const submitInFlightRef = useRef(false);
  const [teamPage, setTeamPage] = useState(1);
  const [teamPageSize, setTeamPageSize] = useState(25);

  useEffect(() => {
    apiFetch("/api/team-members")
      .then(res => res.json())
      .then(data => setMembers(Array.isArray(data) ? data : []))
      .catch(err => {
        console.error("Failed to load team members", err);
        setMembers([]);
      });

    apiFetch("/api/branches")
      .then(res => res.json())
      .then(data => setBranches(Array.isArray(data) ? data : []))
      .catch(err => {
        console.error("Failed to load branches", err);
        setBranches([]);
      });
  }, []);

  const branchesById = useMemo(() => {
    const map = new Map();
    branches.forEach(branch => {
      const key = String(branch.publicId || branch.id || "").trim();
      if (key) map.set(key, branch);
    });
    return map;
  }, [branches]);

  const visibleMembers = useMemo(() => {
    if (!search.trim()) return members;
    const query = search.toLowerCase();
    return members.filter(member => {
      const fullName = `${member.firstName || ""} ${member.lastName || ""}`.toLowerCase();
      return (
        fullName.includes(query) ||
        (member.email || "").toLowerCase().includes(query) ||
        (member.phone || "").toLowerCase().includes(query) ||
        (member.jobTitle || "").toLowerCase().includes(query) ||
        (member.country || "").toLowerCase().includes(query)
      );
    });
  }, [members, search]);

  useEffect(() => { setTeamPage(1); }, [visibleMembers]);

  const pagedMembers = useMemo(
    () => visibleMembers.slice((teamPage - 1) * teamPageSize, teamPage * teamPageSize),
    [visibleMembers, teamPage, teamPageSize]
  );

  const closePanel = () => {
    setPanelOpen(false);
    setEditingId(null);
    setFormError("");
    setForm(emptyForm);
  };

  const openCreate = () => {
    setEditingId(null);
    setFormError("");
    setForm(emptyForm);
    setPanelOpen(true);
  };

  const openEdit = member => {
    const incomingLocations = Array.isArray(member.locations) ? member.locations : [];
    const normalizedLocations = incomingLocations
      .map(location => {
        const value = String(location || "").trim();
        if (!value) return "";
        if (branchesById.has(value)) return value;
        const match = branches.find(branch => String(branch.branchName || "").toLowerCase() === value.toLowerCase());
        return match ? String(match.publicId || match.id || "") : "";
      })
      .filter(Boolean);

    setEditingId(member.publicId || String(member.id));
    setForm({
      ...emptyForm,
      ...member,
      allowBookings: member.allowBookings === undefined ? true : !!member.allowBookings,
      gender: normalizeTeamMemberGender(member.gender),
      locations: normalizedLocations
    });
    setFormError("");
    setPanelOpen(true);
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAvatarChange = e => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setFormError("Please choose an image file.");
      e.target.value = "";
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setFormError("Image size must be under 2 MB.");
      e.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setFormError("");
      setForm(prev => ({
        ...prev,
        avatarUrl: typeof reader.result === "string" ? reader.result : prev.avatarUrl
      }));
    };
    reader.onerror = () => {
      setFormError("Could not read the selected image.");
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const clearAvatar = () => {
    setForm(prev => ({
      ...prev,
      avatarUrl: ""
    }));
  };

  const toggleBranch = branchId => {
    const normalized = String(branchId || "");
    if (!normalized) return;
    setForm(prev => {
      const exists = prev.locations.includes(normalized);
      return {
        ...prev,
        locations: exists ? prev.locations.filter(id => id !== normalized) : [...prev.locations, normalized]
      };
    });
  };

  const getBranchNames = locations => {
    const list = Array.isArray(locations) ? locations : [];
    return list
      .map(location => {
        const value = String(location || "").trim();
        if (!value) return "";
        const branch = branchesById.get(value);
        if (branch?.branchName) return branch.branchName;
        return value;
      })
      .filter(Boolean);
  };

  const submit = async e => {
    e.preventDefault();
    if (submitInFlightRef.current || isSaving) return;
    
    submitInFlightRef.current = true;
    setIsSaving(true);

    const releaseSubmitLock = () => {
      submitInFlightRef.current = false;
      setIsSaving(false);
    };

    setFormError("");

    const firstName = form.firstName.trim();
    const email = form.email.trim();
    const phone = form.phone.trim();

    if (!firstName || !email || !phone) {
      setFormError("First name, email and phone are required.");
      releaseSubmitLock();
      return;
    }

    const payload = {
      ...emptyForm,
      ...form,
      firstName,
      lastName: form.lastName.trim(),
      gender: normalizeTeamMemberGender(form.gender),
      country: String(form.country || "").trim(),
      email,
      countryCode: form.countryCode.trim() || "+971",
      phone,
      jobTitle: form.jobTitle.trim(),
      avatarUrl: String(form.avatarUrl || "").trim(),
      locations: Array.isArray(form.locations) ? form.locations.filter(Boolean) : [],
      allowBookings: !!form.allowBookings
    };

    try {
      let saved;
      if (editingId) {
        const res = await apiFetch(`/api/team-members/${editingId}`, {
          method: "PUT",
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.error || `Server error ${res.status}`);
        }
        saved = await res.json();
        setMembers(prev => prev.map(member => (String(member.publicId || member.id) === String(editingId) ? saved : member)));
        if (onMemberSaved) onMemberSaved(saved);
      } else {
        const res = await apiFetch("/api/team-members", {
          method: "POST",
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.error || `Server error ${res.status}`);
        }
        saved = await res.json();
        setMembers(prev => [saved, ...prev]);
        if (onMemberSaved) onMemberSaved(saved);
      }
      closePanel();
      releaseSubmitLock();
    } catch (err) {
      console.error("Failed to save team member", err);
      setFormError(err.message || "Could not save team member");
      releaseSubmitLock();
    }
  };

  const deleteMember = async id => {
    if (!window.confirm("Delete this team member?")) return;
    try {
      const res = await apiFetch(`/api/team-members/${id}`, { method: "DELETE" });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `Delete failed (${res.status})`);
      }
      setMembers(prev => prev.filter(member => String(member.publicId || member.id) !== String(id)));
      if (onMemberDeleted) onMemberDeleted(id);
      if (String(editingId || "") === String(id)) closePanel();
    } catch (err) {
      console.error("Failed to delete team member", err);
      alert(err.message || "Could not delete team member");
    }
  };

  return (
    <div className="team-members-page page main-content">
      <div className="team-members-header">
        <div>
          <h1>Team Members</h1>
          <p>Manage staff profiles, branch assignment, and role-based productivity setup.</p>
        </div>
        <div className="team-members-actions">
          <input
            className="team-members-search"
            type="text"
            placeholder="Search by name, phone, email or job title"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {canDo(currentUser, "staff", "add") && (
            <button className="open-panel-btn" onClick={openCreate}>Add Team Member</button>
          )}
        </div>
      </div>

      <div className="team-members-list">
        {visibleMembers.length === 0 ? (
          <p>No team members found.</p>
        ) : (
          <table className="team-members-table">
            <thead>
              <tr>
                <th>Member</th>
                <th>Mobile</th>
                <th>Email</th>
                <th>Nationality</th>
                <th>Job Title</th>
                <th>Branches</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedMembers.map((member, index) => {
                const branchNames = getBranchNames(member.locations);
                return (
                  <tr key={member.publicId || member.id || index}>
                    <td data-label="Member">
                      <div className="team-member-cell">
                        <img
                          src={getTeamMemberAvatar(member)}
                          alt={getTeamMemberName(member)}
                          className="team-member-avatar"
                          onError={event => {
                            event.currentTarget.onerror = null;
                            event.currentTarget.src = createInlineFallbackAvatar({
                              name: getTeamMemberName(member),
                              gender: member.gender
                            });
                          }}
                        />
                        <span>{getTeamMemberName(member)}</span>
                      </div>
                    </td>
                    <td data-label="Mobile">{`${member.countryCode || ""} ${member.phone || ""}`.trim() || "-"}</td>
                    <td data-label="Email">{member.email || "-"}</td>
                    <td data-label="Nationality">{member.country || "-"}</td>
                    <td data-label="Job Title">{member.jobTitle || "-"}</td>
                    <td data-label="Branches">{branchNames.length ? branchNames.join(", ") : "-"}</td>
                    <td data-label="Actions" className="actions-cell">
                      {canDo(currentUser, "staff", "edit") && (
                        <button
                          className="icon-btn edit-btn"
                          title="Edit"
                          onClick={() => openEdit(member)}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" fill="#fff" opacity="0.9"/>
                            <path d="M20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" fill="#fff" opacity="0.9"/>
                          </svg>
                        </button>
                      )}
                      {canDo(currentUser, "staff", "delete") && (
                        <button
                          className="icon-btn delete-btn"
                          title="Delete"
                          onClick={() => deleteMember(member.publicId || String(member.id))}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                            <path d="M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6v12z" fill="#fff" opacity="0.95"/>
                            <path d="M19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="#fff" opacity="0.95"/>
                          </svg>
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <Paginator
          page={teamPage}
          pageSize={teamPageSize}
          total={visibleMembers.length}
          onPageChange={setTeamPage}
          onPageSizeChange={size => { setTeamPageSize(size); setTeamPage(1); }}
        />
      </div>

      <SidePanel
        isOpen={panelOpen}
        onClose={closePanel}
        className="panel-medium team-members-form-panel"
      >
        <div className="team-member-form-container">
          <h2>{editingId ? "Edit Team Member" : "Add Team Member"}</h2>
          <form className="team-member-form" onSubmit={submit}>
            <div className="team-member-photo-section">
              <img
                src={getTeamMemberAvatar(form)}
                alt={getTeamMemberName(form)}
                className="team-member-photo-preview"
                onError={event => {
                  event.currentTarget.onerror = null;
                  event.currentTarget.src = createInlineFallbackAvatar({
                    name: getTeamMemberName(form),
                    gender: form.gender
                  });
                }}
              />
              <div className="team-member-photo-actions">
                <label className="team-member-upload-btn">
                  <input type="file" accept="image/*" onChange={handleAvatarChange} />
                  Upload Photo
                </label>
                {form.avatarUrl && (
                  <button type="button" className="team-member-clear-photo-btn" onClick={clearAvatar}>
                    Remove Photo
                  </button>
                )}
              </div>
            </div>

            <div className="team-member-grid two-col">
              <input name="firstName" placeholder="First name *" value={form.firstName} onChange={handleChange} required />
              <input name="lastName" placeholder="Last name" value={form.lastName} onChange={handleChange} />
            </div>

            <div className="team-member-grid two-col">
              <select name="gender" value={form.gender} onChange={handleChange}>
                <option value="female">Female</option>
                <option value="male">Male</option>
              </select>
              <input type="email" name="email" placeholder="Email *" value={form.email} onChange={handleChange} required />
            </div>

            <div className="team-member-grid two-col">
              <select name="country" value={form.country} onChange={handleChange}>
                {NATIONALITY_OPTIONS.map(option => (
                  <option key={option || "empty"} value={option}>
                    {option || "Select nationality"}
                  </option>
                ))}
              </select>
              <input name="jobTitle" placeholder="Job title" value={form.jobTitle} onChange={handleChange} />
            </div>

            <div className="team-member-grid two-col">
              <input name="countryCode" placeholder="Country code" value={form.countryCode} onChange={handleChange} />
              <div></div>
            </div>

            <div className="team-member-grid two-col">
              <input name="phone" placeholder="Phone *" value={form.phone} onChange={handleChange} required />
              <div></div>
            </div>

            <div className="form-section-title">Online Booking</div>
            <label className="branch-option allow-bookings-toggle">
              <input
                type="checkbox"
                checked={!!form.allowBookings}
                onChange={e => setForm(prev => ({ ...prev, allowBookings: e.target.checked }))}
              />
              <span>Allow this member to appear in customer online booking</span>
            </label>

            <div className="form-section-title">Branches</div>
            <div className="branch-link-box">
              {branches.length === 0 ? (
                <p>No branches available.</p>
              ) : (
                branches.map(branch => {
                  const branchId = String(branch.publicId || branch.id || "");
                  if (!branchId) return null;
                  return (
                    <label key={branchId} className="branch-option">
                      <input
                        type="checkbox"
                        checked={form.locations.includes(branchId)}
                        onChange={() => toggleBranch(branchId)}
                      />
                      <span>{branch.branchName || branch.branchCode || branchId}</span>
                    </label>
                  );
                })
              )}
            </div>

            {editingId && (form.createdBy || form.createdAt || form.updatedBy || form.updatedAt) && (
              <div className="audit-trail">
                {(form.createdBy || form.createdAt) && (
                  <span className="audit-item"><strong>Created by:</strong> {form.createdBy || "\u2014"}{form.createdAt ? ` \u00b7 ${new Date(form.createdAt).toLocaleString()}` : ""}</span>
                )}
                {(form.updatedBy || form.updatedAt) && (
                  <span className="audit-item"><strong>Updated by:</strong> {form.updatedBy || "\u2014"}{form.updatedAt ? ` \u00b7 ${new Date(form.updatedAt).toLocaleString()}` : ""}</span>
                )}
              </div>
            )}
            {formError && <p className="team-member-form-error">{formError}</p>}

            <div className="team-member-form-actions">
              {(editingId ? canDo(currentUser, "staff", "edit") : canDo(currentUser, "staff", "add")) && (
                <button type="submit" disabled={isSaving}>{isSaving ? "Saving..." : (editingId ? "Save Member" : "Create Member")}</button>
              )}
              {editingId && canDo(currentUser, "staff", "delete") && (
                <button
                  type="button"
                  className="team-member-delete-btn"
                  onClick={() => deleteMember(editingId)}
                >
                  Delete Member
                </button>
              )}
            </div>
          </form>
        </div>
      </SidePanel>
    </div>
  );
};

export default TeamMembers;